import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { iaRa } from 'lib/helpers/utility';

import { sheetMetrics } from './docStyles';
import { packBlocks, pageBodyHeight, tableUnits } from './paginate';

const measureHeight = (node) => (node ? Math.ceil(node.getBoundingClientRect().height) : 0);

const useResizeEffect = (ref, update) => {
  const updateRef = useRef(update);
  updateRef.current = update;

  useLayoutEffect(() => {
    const element = ref.current;

    if (!element || typeof ResizeObserver === 'undefined') return undefined;

    updateRef.current();

    const observer = new ResizeObserver(() => updateRef.current());

    observer.observe(element);

    return () => observer.disconnect();
  }, [ref]);
};

export const useDocumentPagesLayout = (items, { landscape = false } = {}) => {
  const { sheetWidth } = sheetMetrics(landscape);

  const [layout, setLayout] = useState(null);
  const [fitScale, setFitScale] = useState(1);
  const [stackHeight, setStackHeight] = useState(0);

  const wrapperRef = useRef(null);
  const stackRef = useRef(null);
  const measureRef = useRef(null);
  const nodeRefs = useRef({});
  const signatureRef = useRef('');
  const measureLayoutRef = useRef(null);

  const setNodeRef = useCallback(
    (key, part) => (node) => {
      nodeRefs.current[`${key}:${part}`] = node;
    },
    []
  );

  const measureLayout = () => {
    const get = (key, part) => nodeRefs.current[`${key}:${part}`];

    const headerFirst = measureHeight(get('__chrome', 'header-first'));
    const headerRest = measureHeight(get('__chrome', 'header-rest'));
    const footer = measureHeight(get('__chrome', 'footer'));

    const measured = items.map((block) => {
      const units = block.table ? tableUnits(block.table.groups) : null;
      const rows = units
        ? iaRa(
            Array.from(get(block.key, 'rows')?.querySelectorAll('tbody > tr[data-doc-unit]') || [])
          ).map((row) => ({ height: measureHeight(row) }))
        : null;

      return {
        key: block.key,
        height: measureHeight(get(block.key, 'node')),
        leadingHeight: block.leading ? measureHeight(get(block.key, 'leading')) : 0,
        headerHeight: units ? measureHeight(get(block.key, 'header')) : 0,
        units: units && rows && rows.length === units.length ? rows : null
      };
    });

    const capacityFor = (pageIndex) =>
      pageBodyHeight(landscape) - (pageIndex === 0 ? headerFirst : headerRest) - footer;

    const pages = packBlocks({ blocks: measured, capacityFor });
    const signature = JSON.stringify({ headerFirst, headerRest, footer, measured, pages });

    if (signature !== signatureRef.current) {
      signatureRef.current = signature;
      setLayout({ pages });
    }
  };

  useLayoutEffect(() => {
    measureLayoutRef.current = measureLayout;
    measureLayout();
  });

  useResizeEffect(measureRef, () => measureLayoutRef.current?.());

  useResizeEffect(wrapperRef, () => {
    const available = wrapperRef.current?.clientWidth;
    setFitScale(available && available < sheetWidth ? available / sheetWidth : 1);
  });

  useResizeEffect(stackRef, () => setStackHeight(stackRef.current?.offsetHeight || 0));

  return { layout, fitScale, stackHeight, wrapperRef, stackRef, measureRef, setNodeRef };
};
